import { useCallback, useEffect, useMemo, useState } from 'react';
import { bulkResolveQueueItems } from './services/queue.service';
import { fetchProgramCourses, searchPrograms } from './services/program-review.service';
import { SEARCH_MIN_LENGTH, SEARCH_DEBOUNCE_MS } from './constants/lms-course-review.constants';
import {
  PROGRAM_COURSE_FILTER,
  PROGRAM_COURSE_STATUS,
  SEARCH_MODE,
  toProgramBulkResolveItems,
  toProgramCourseItem,
  toProgramSearchItem,
  toQueueItemFromProgramCourse,
} from './utils/program-review.utils';

const useProgramReviewHook = ({ onOpenResolution, onToast } = {}) => {
  const [searchMode, setSearchMode] = useState(SEARCH_MODE.COURSE);
  const [programQuery, setProgramQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [programsError, setProgramsError] = useState(null);

  const [selectedProgram, setSelectedProgram] = useState(null);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [coursesError, setCoursesError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(PROGRAM_COURSE_FILTER.ALL);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkLinking, setBulkLinking] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(programQuery.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [programQuery]);

  const resetProgramSelection = useCallback(() => {
    setSelectedProgram(null);
    setCourses([]);
    setCoursesError(null);
    setStatusFilter(PROGRAM_COURSE_FILTER.ALL);
    setSelectedIds(new Set());
  }, []);

  const switchSearchMode = useCallback(
    (mode) => {
      setSearchMode(mode);
      if (mode === SEARCH_MODE.COURSE) {
        setProgramQuery('');
        setDebouncedQuery('');
        setPrograms([]);
        setProgramsError(null);
        resetProgramSelection();
      }
    },
    [resetProgramSelection]
  );

  useEffect(() => {
    if (searchMode !== SEARCH_MODE.PROGRAM) return undefined;
    if (selectedProgram) return undefined;
    if (debouncedQuery.length < SEARCH_MIN_LENGTH) {
      setPrograms([]);
      setProgramsError(null);
      setProgramsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setProgramsLoading(true);
    setProgramsError(null);

    searchPrograms(debouncedQuery)
      .then((rows) => {
        if (!cancelled) setPrograms(rows.map(toProgramSearchItem));
      })
      .catch((error) => {
        if (!cancelled) {
          setPrograms([]);
          setProgramsError(error?.message || 'Failed to search programs');
        }
      })
      .finally(() => {
        if (!cancelled) setProgramsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, searchMode, selectedProgram]);

  const loadCourses = useCallback(async (program) => {
    setCoursesLoading(true);
    setCoursesError(null);
    setSelectedIds(new Set());
    try {
      const rows = await fetchProgramCourses(program.resourceId);
      setCourses(rows.map(toProgramCourseItem));
    } catch (error) {
      setCourses([]);
      setCoursesError(error?.message || 'Failed to load program courses');
    } finally {
      setCoursesLoading(false);
    }
  }, []);

  const openProgram = useCallback(
    async (program) => {
      setSelectedProgram(program);
      await loadCourses(program);
    },
    [loadCourses]
  );

  const backToPrograms = useCallback(() => {
    resetProgramSelection();
  }, [resetProgramSelection]);

  const coverage = useMemo(
    () => ({
      total: courses.length,
      pending: courses.filter((c) => c.status === PROGRAM_COURSE_STATUS.PENDING).length,
      linked: courses.filter((c) => c.status === PROGRAM_COURSE_STATUS.LINKED).length,
      notOnCanvas: courses.filter((c) => c.status === PROGRAM_COURSE_STATUS.NOT_ON_CANVAS).length,
    }),
    [courses]
  );

  // Prefer live course classification once loaded; fall back to search-card coverage while loading.
  const displayCoverage = coursesLoading && selectedProgram?.coverage ? selectedProgram.coverage : coverage;

  const visibleCourses = useMemo(() => {
    if (statusFilter === PROGRAM_COURSE_FILTER.ALL) return courses;
    return courses.filter((course) => course.status === statusFilter);
  }, [courses, statusFilter]);

  const pendingVisible = useMemo(
    () =>
      visibleCourses.filter(
        (course) => course.status === PROGRAM_COURSE_STATUS.PENDING && course.catalogId != null
      ),
    [visibleCourses]
  );

  const toggleCourseSelected = useCallback((resourceId, status) => {
    if (status !== PROGRAM_COURSE_STATUS.PENDING) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) next.delete(resourceId);
      else next.add(resourceId);
      return next;
    });
  }, []);

  const selectAllPending = useCallback(() => {
    setSelectedIds(new Set(pendingVisible.map((course) => course.resourceId)));
  }, [pendingVisible]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const selectedCourses = useMemo(
    () => courses.filter((course) => selectedIds.has(course.resourceId)),
    [courses, selectedIds]
  );

  const openResolve = useCallback(
    (course) => {
      if (!course?.catalogId || !onOpenResolution) return;
      onOpenResolution(toQueueItemFromProgramCourse(course));
    },
    [onOpenResolution]
  );

  const linkSelected = useCallback(async () => {
    const items = toProgramBulkResolveItems(selectedCourses);
    if (!items.length) return;

    setBulkLinking(true);
    try {
      await bulkResolveQueueItems(items);
      onToast?.({
        type: 'success',
        message: `Linked ${items.length} course${items.length === 1 ? '' : 's'}.`,
      });
      clearSelection();
      if (selectedProgram) {
        await loadCourses(selectedProgram);
      }
    } catch (error) {
      onToast?.({
        type: 'error',
        message: error?.message || 'Failed to link selected courses',
      });
    } finally {
      setBulkLinking(false);
    }
  }, [clearSelection, loadCourses, onToast, selectedCourses, selectedProgram]);

  const refreshAfterResolve = useCallback(async () => {
    if (selectedProgram) {
      await loadCourses(selectedProgram);
    }
  }, [loadCourses, selectedProgram]);

  return {
    searchMode,
    switchSearchMode,
    isProgramMode: searchMode === SEARCH_MODE.PROGRAM,
    programQuery,
    setProgramQuery,
    programs,
    programsLoading,
    programsError,
    selectedProgram,
    openProgram,
    backToPrograms,
    courses: visibleCourses,
    allCourses: courses,
    coursesLoading,
    coursesError,
    statusFilter,
    setStatusFilter,
    coverage: displayCoverage,
    selectedIds,
    toggleCourseSelected,
    selectAllPending,
    clearSelection,
    selectedCount: selectedIds.size,
    linkSelected,
    bulkLinking,
    openResolve,
    refreshAfterResolve,
    canSelectAllPending:
      statusFilter !== PROGRAM_COURSE_FILTER.LINKED &&
      statusFilter !== PROGRAM_COURSE_FILTER.NOT_ON_CANVAS &&
      pendingVisible.length > 0 &&
      pendingVisible.some((course) => !selectedIds.has(course.resourceId)),
  };
};

export default useProgramReviewHook;
